import { empty } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { Component, Watch } from 'vue-property-decorator'

import { Work, Item, isSearchResult } from '@skinsoft/types'
import { expressionSelectors, itemSelectors, manifestationSelectors, workSelectors } from '@skinsoft/selectors'
import {
    ExpressionDataStore,
    ManifestationDataStore,
    WorkDataStore,
} from '@skinsoft/entity'
import { searchResultSelectors } from '@skinsoft/search-data-store'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { Author } from './author'
import { PropType } from 'vue'

enum States {
    PENDING = 'pending',
    READY = 'ready',
    ERROR = 'error',
}

@Component({
    components: {
        sAuthor: Author,
    },
    props: {
        item: {
            type: Object as PropType<Item>,
            required: true,
        },
    },
    template: require('./description.html'),
})
export class Description extends ReactiveVue {
    public item: Item

    private itemIsSearchResult: boolean = false

    public $manifestationDataStore: ManifestationDataStore;
    public $expressionDataStore: ExpressionDataStore;
    public $workDataStore: WorkDataStore;

    public state: States = States.PENDING;
    public work: Work = null as any;
    public workSelectors = workSelectors;
    private searchResultSelectors = searchResultSelectors

    private authorReady = false;

    public created() {
        this.itemIsSearchResult = isSearchResult(this.item)
        this.subscribe()
    }

    public onAuthorReady() {
        this.authorReady = true;
        this.processReady();
    }

    public formatFromUnit(unit) {
        switch (unit) {
            case 'year':
                return unit;
            default:
                return 'short';
        }
    }

    private processReady() {
        if (this.authorReady && this.state !== States.PENDING) {
            this.$emit('ready');
        }
    }

    private subscribe() {
        if(this.itemIsSearchResult) {
            this.authorReady = true
            this.state = States.READY
        }
        else {
            const manifestationId = itemSelectors.manifestation(this.item);
            if (!manifestationId) {
                this.authorReady = true; // as we don't have any manifestation available, author won't be rendered at all
                this.state = States.READY;
                return;
            }
        
            this.state = States.PENDING;
        
            this.subscribeTo(
                this.$manifestationDataStore.fetchOneWithItemType(manifestationId, this.item.type).pipe(
                    switchMap(manifestation => {
                        const expressionId = manifestationSelectors.expression(manifestation);
        
                        if (!expressionId) {
                            this.authorReady = true; // as we don't have any expression available, author won't be rendered at all
                            this.state = States.READY;
                            return empty();
                        }
        
                        return this.$expressionDataStore.fetchOne(expressionId);
                    }),
                    switchMap(expression => {
                        const workId = expressionSelectors.work(expression);
    
                        if (!workId) {
                            this.authorReady = true; // as we don't have any work available, author won't be rendered at all
                            this.state = States.READY;
                            return empty();
                        }
        
                        return this.$workDataStore.fetchOne(workId);
                    }),
                    catchError(error => {
                        // tslint:disable-next-line:no-console
                        if (error.status === 403) {
                            // trying to fetch manifestation without permission (from another workspace, ...)
                            this.state = States.READY
                            this.authorReady = true
                        } else {
                            console.error(error);
                            this.state = States.ERROR;
                        }
        
                        return empty();
                    }),
                ),
                work => {
                    this.work = work;
                    this.state = States.READY;
                },
                'manifestation',
            )
        }
    }

    @Watch('item')
    private onItemChange() {
        this.itemIsSearchResult = isSearchResult(this.item)
        this.unsubscribeFrom('manifestation');
        this.subscribe();
    }

    @Watch('state')
    private onStateChange() {
        this.processReady();
    }
}
